import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Intervention, InterventionRejectRequest } from '../../generated/api/types.gen.js';
import { zInterventionRejectRequest } from '../../generated/api/zod.gen.js';
import {
  getInterventionQueryKey,
  listInterventionsQueryKey,
  rejectInterventionMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { Button, Modal, useToast } from '../../components/ui/index.js';
import { Form, SubmitButton, TextAreaFormField, useZodForm } from '../../components/form/index.js';

interface ActionRequestRejectModalProps {
  /** The request being rejected; `null` keeps the dialog closed. */
  request: Intervention | null;
  onClose: () => void;
}

interface RejectFormProps {
  request: Intervention;
  onClose: () => void;
}

const RejectForm: React.FC<RejectFormProps> = ({ request, onClose }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const reject = useMutation(rejectInterventionMutation());
  const form = useZodForm(zInterventionRejectRequest, { defaultValues: { reason: '' } });
  const path = { id: String(request.id) };

  const submit = async (body: InterventionRejectRequest) => {
    await reject.mutateAsync({ path, body });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getInterventionQueryKey({ path }) }),
      queryClient.invalidateQueries({ queryKey: listInterventionsQueryKey() }),
    ]);
    notify('Request rejected');
    onClose();
  };

  return (
    <Form form={form} onSubmit={submit}>
      <TextAreaFormField
        name="reason"
        label="Reason"
        rows={4}
        description="Recorded on the request's timeline."
        required
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <SubmitButton variant="danger">Reject request</SubmitButton>
      </div>
    </Form>
  );
};

/** Asks for the reason before rejecting an action request. */
export const ActionRequestRejectModal: React.FC<ActionRequestRejectModalProps> = ({ request, onClose }) => (
  <Modal open={request !== null} onClose={onClose} title="Reject request">
    {request && <RejectForm key={request.id} request={request} onClose={onClose} />}
  </Modal>
);
